<template>
	<section class="overflow-hidden text-white rounded-lg bg-neutral-950">
		<div class="flex items-center justify-between gap-4 p-6">
			<h2 class="mb-0 text-lg font-extrabold text-white uppercase">
				{{ $t('job-offer-creation.review.role-profile') }}
			</h2>
			<div
				class="flex items-center space-x-2 cursor-pointer shrink-0"
				@click="modalOpen = true"
			>
				<div class="flex items-center justify-center">
					<Icon :icon="['far', 'info-circle']" />
				</div>
				<span class="hidden md:inline-block">
					{{ modalTrigger }}
				</span>
			</div>
		</div>

		<OverviewSummary
			v-if="hasSummary"
			:summary="summaryDisplay"
			bg-class="bg-neutral-900"
			class="mx-6 mb-6"
		/>

		<div
			v-if="personalityRows.length"
			class="grid gap-3 px-6 pb-6 sm:grid-cols-3"
		>
			<PersonalityTraitCompactCard
				v-for="row in personalityRows"
				:key="row.key"
				:row="row"
			/>
		</div>

		<div v-else class="p-6 border-t border-neutral-800 bg-neutral-900">
			<div
				class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
			>
				<p class="mb-0 text-sm text-neutral-200">
					{{ $t('job-offer-creation.review.missing-personality') }}
				</p>
			</div>
		</div>

		<TraitsInfoModal v-model="modalOpen" wheel-mode="job" />
	</section>
</template>

<script>
import Icon from '~/components/ui/common/Icon'
import { traitColors } from '~/components/Icons/traits'
import { TRAIT_LABELS } from '~/components/Personality/utils/personalityConstants'
import {
	getProfileDisplayWithWrapper,
	getTopTraits
} from '~/components/Personality/utils/personalityProfileMapper'
import {
	OverviewSummary,
	PersonalityTraitCompactCard,
	TraitsInfoModal
} from './_internals'

export default {
	name: 'PersonalityResultsCompact',
	components: {
		Icon,
		OverviewSummary,
		PersonalityTraitCompactCard,
		TraitsInfoModal
	},
	props: {
		personality: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			modalOpen: false
		}
	},
	computed: {
		modalTrigger() {
			return this.$t('personality_results.overview.header.modal_trigger')
		},
		personalityBreakdown() {
			const total = this.personality.length

			if (!total) {
				return {}
			}

			const counts = this.personality.reduce((items, type) => {
				items[type] = (items[type] || 0) + 1
				return items
			}, {})

			return Object.fromEntries(
				Object.entries(counts).map(([type, count]) => [
					type,
					Math.round((count / total) * 100)
				])
			)
		},
		summaryDisplay() {
			const profileDisplay = getProfileDisplayWithWrapper(
				this.personalityBreakdown,
				'hiring_manager'
			)

			return {
				workplaceSignalKey: profileDisplay?.workplaceSignal || '',
				signalDescriptionKey: profileDisplay?.signalDescription || '',
				approach: profileDisplay?.approach
			}
		},
		hasSummary() {
			return Boolean(
				this.summaryDisplay.workplaceSignalKey &&
					this.summaryDisplay.signalDescriptionKey
			)
		},
		personalityRows() {
			return getTopTraits(this.personalityBreakdown, 3).map(
				({ trait, percent }) => ({
					key: trait,
					label: TRAIT_LABELS[trait],
					sloganKey: `personality_results.trait.${trait}.slogan`,
					percent,
					color: traitColors[trait] || '#FFFFFF'
				})
			)
		}
	}
}
</script>
